'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState } from 'react';
import { useLocalization } from '@/app/(app)/_providers/localization/LocalizationProvider';
import { isError } from '@/utilities/serverError';
import { cn } from '@/utilities/ui';
import { useMutation } from '@tanstack/react-query';
import { Check, Copy, Loader2, RefreshCw, X } from 'lucide-react';
import { toast } from 'sonner';
import { sendConfigMessage } from './actions';
import { ConfigPanelPdfExport } from './ConfigPanelPdfExport';
import { useMermaidPng } from './useMermaidPng';

// ─── Types ──────────────────────────────────────────────

export type ConfigOverviewItem = {
  label: string;
  value: string;
  mono?: boolean;
};
export type EquipmentCategory = 'display' | 'audio' | 'control' | 'network' | 'source' | 'processing' | 'infrastructure' | 'mounting' | 'cabling';
export type ConfigEquipmentItem = {
  name: string;
  meta: string;
  quantity: number;
  category: EquipmentCategory;
};
export type ConfigFlowNode = {
  name: string;
  label: string;
};
export type ConfigNote = {
  tag: string;
  content: string;
};
export type ConfigData = {
  status?: string;
  overview: ConfigOverviewItem[];
  equipment: ConfigEquipmentItem[];
  signalFlow: '';
  notes: ConfigNote[];
  updatedAt?: string;
};

// ─── Category dot colors ────────────────────────────────

const CATEGORY_DOT: Record<EquipmentCategory, string> = {
  display: 'bg-blue-500',
  audio: 'bg-green-500',
  control: 'bg-amber-500',
  network: 'bg-violet-500',
  source: 'bg-cyan-500',
  processing: 'bg-rose-500',
  infrastructure: 'bg-slate-500',
  mounting: 'bg-orange-500',
  cabling: 'bg-teal-500'
};
const VALID_CATEGORIES = new Set<EquipmentCategory>(['display', 'audio', 'control', 'network', 'source', 'processing', 'infrastructure', 'mounting', 'cabling']);
function mapCategory(raw: string): EquipmentCategory {
  const lower = (raw?.toLowerCase() ?? '') as EquipmentCategory;
  return VALID_CATEGORIES.has(lower) ? lower : 'control';
}
function capitalize(s: string) {
  return s.charAt(0).toUpperCase() + s.slice(1);
}
function mapLlmResponseToConfigData(json: any, labels: {
  project: string;
  type: string;
  dimensions: string;
  budget: string;
  statusDraft: string;
  updatedPrefix: string;
}): ConfigData {
  const project = json.project || {};
  const equipment = json.equipment || [];
  const signalFlowRaw = typeof json.signal_flow === 'string' ? json.signal_flow : '';
  const notesRaw = json.notes || [];
  const overview: ConfigOverviewItem[] = [];
  if (project.name) overview.push({
    label: labels.project,
    value: project.name
  });
  let typeValue = project.type || '';
  if (project.capacity) typeValue += ` — ${project.capacity} PAX`;
  if (typeValue) overview.push({
    label: labels.type,
    value: typeValue
  });
  const dims = project.dimensions;
  if (dims && (dims.width || dims.depth || dims.height)) {
    const parts = [dims.width, dims.depth, dims.height].filter(v => v != null).map(v => `${v}`);
    const unit = dims.unit || 'm';
    overview.push({
      label: labels.dimensions,
      value: parts.join(` × `) + unit,
      mono: true
    });
  }
  const budget = project.budget;
  if (budget && (budget.min != null || budget.max != null)) {
    const curr = budget.currency || '€';
    const parts: string[] = [];
    if (budget.min != null) parts.push(budget.min.toLocaleString());
    if (budget.max != null) parts.push(budget.max.toLocaleString());
    overview.push({
      label: labels.budget,
      value: `${curr} ${parts.join(' – ')}`,
      mono: true
    });
  }
  const equipmentMap = new Map<string, any>();
  const mappedEquipment: ConfigEquipmentItem[] = equipment.map((eq: any) => {
    equipmentMap.set(eq.id, eq);
    const metaParts: string[] = [];
    if (eq.specs?.size) metaParts.push(eq.specs.size);
    if (eq.specs?.resolution) metaParts.push(eq.specs.resolution);
    if (eq.specs?.brightness) metaParts.push(eq.specs.brightness);
    if (eq.specs?.pixel_pitch) metaParts.push(eq.specs.pixel_pitch);
    if (eq.specs?.connectivity?.length) metaParts.push(eq.specs.connectivity.join(', '));
    if (metaParts.length === 0 && eq.description) metaParts.push(eq.description);
    return {
      name: [eq.manufacturer, eq.model].filter(Boolean).join(' '),
      meta: metaParts.join(' · ') || eq.description || eq.role || '',
      quantity: eq.quantity || 1,
      category: mapCategory(eq.category)
    };
  });
  const mappedNotes: ConfigNote[] = notesRaw.map((note: any) => ({
    tag: (note.category || 'general').toUpperCase(),
    content: note.text || ''
  }));
  return {
    status: project.status ? capitalize(project.status) : labels.statusDraft,
    overview,
    equipment: mappedEquipment,
    signalFlow: signalFlowRaw,
    notes: mappedNotes,
    updatedAt: `${labels.updatedPrefix} ${new Date().toLocaleTimeString()}`
  };
}

// ─── Component ──────────────────────────────────────────

export function ConfigPanel(t0) {
  const $ = _c(116);
  const {
    isOpen,
    onClose,
    messages,
    chatIsPending
  } = t0;
  const {
    messages: i18n
  } = useLocalization();
  const [copied, setCopied] = useState(false);
  const [configData, setConfigData] = useState(null);
  const [hadError, setHadError] = useState(false);
  const {
    pngDataUrl: signalFlowPng,
    error: signalFlowError,
    loading: signalFlowLoading,
    refresh: refreshSignalFlow
  } = useMermaidPng(configData?.signalFlow ?? "");
  let t1;
  if ($[0] !== i18n.config.assistantPrefix || $[1] !== i18n.config.budgetLabel || $[2] !== i18n.config.dimensionsLabel || $[3] !== i18n.config.noConversation || $[4] !== i18n.config.projectLabel || $[5] !== i18n.config.statusDraft || $[6] !== i18n.config.typeLabel || $[7] !== i18n.config.updatedPrefix || $[8] !== i18n.config.userPrefix || $[9] !== messages) {
    t1 = async () => {
      if (messages.length === 0) {
        throw new Error(i18n.config.noConversation);
      }
      const conversationText = messages.map(m => `${m.role === "user" ? i18n.config.userPrefix : i18n.config.assistantPrefix}: ${m.content}`).join("\n\n");
      const result = await sendConfigMessage({
        messages: conversationText
      });
      if (isError(result)) {
        throw new Error(result.message);
      }
      const rawText = "text" in result ? result.text : String(result);
      const cleaned = rawText.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
      const parsed = JSON.parse(cleaned);
      return mapLlmResponseToConfigData(parsed, {
        project: i18n.config.projectLabel,
        type: i18n.config.typeLabel,
        dimensions: i18n.config.dimensionsLabel,
        budget: i18n.config.budgetLabel,
        statusDraft: i18n.config.statusDraft,
        updatedPrefix: i18n.config.updatedPrefix
      });
    };
    $[0] = i18n.config.assistantPrefix;
    $[1] = i18n.config.budgetLabel;
    $[2] = i18n.config.dimensionsLabel;
    $[3] = i18n.config.noConversation;
    $[4] = i18n.config.projectLabel;
    $[5] = i18n.config.statusDraft;
    $[6] = i18n.config.typeLabel;
    $[7] = i18n.config.updatedPrefix;
    $[8] = i18n.config.userPrefix;
    $[9] = messages;
    $[10] = t1;
  } else {
    t1 = $[10];
  }
  let t2;
  if ($[11] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = data => setConfigData(data);
    $[11] = t2;
  } else {
    t2 = $[11];
  }
  let t3;
  if ($[12] !== i18n.config.refreshFailed) {
    t3 = error => {
      toast.error(error.message || i18n.config.refreshFailed);
      setHadError(true);
    };
    $[12] = i18n.config.refreshFailed;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  let t4;
  if ($[14] !== t1 || $[15] !== t3) {
    t4 = {
      mutationFn: t1,
      onSuccess: t2,
      onError: t3
    };
    $[14] = t1;
    $[15] = t3;
    $[16] = t4;
  } else {
    t4 = $[16];
  }
  const configMutation = useMutation(t4);
  let t5;
  if ($[17] !== chatIsPending || $[18] !== configData || $[19] !== configMutation || $[20] !== hadError || $[21] !== isOpen || $[22] !== messages.length) {
    t5 = () => {
      if (isOpen && !configData && !configMutation.isPending && messages.length > 0 && !chatIsPending) {
        if (hadError) {
          return;
        }
        configMutation.mutate();
      }
    };
    $[17] = chatIsPending;
    $[18] = configData;
    $[19] = configMutation;
    $[20] = hadError;
    $[21] = isOpen;
    $[22] = messages.length;
    $[23] = t5;
  } else {
    t5 = $[23];
  }
  let t6;
  if ($[24] !== configData || $[25] !== configMutation || $[26] !== isOpen || $[27] !== messages.length) {
    t6 = [isOpen, configData, configMutation, configMutation.isPending, messages.length];
    $[24] = configData;
    $[25] = configMutation;
    $[26] = isOpen;
    $[27] = messages.length;
    $[28] = t6;
  } else {
    t6 = $[28];
  }
  useEffect(t5, t6);
  let t7;
  if ($[29] !== configData || $[30] !== i18n.config.copiedToClipboard || $[31] !== i18n.config.items || $[32] !== i18n.config.textConfiguration || $[33] !== i18n.config.textEquipment || $[34] !== i18n.config.textNotes || $[35] !== i18n.config.textProjectOverview || $[36] !== i18n.config.textStatus) {
    t7 = () => {
      if (!configData) {
        return;
      }
      const lines = [];
      lines.push(i18n.config.textConfiguration);
      if (configData.status) {
        lines.push(`${i18n.config.textStatus}: ${configData.status}`);
      }
      lines.push("");
      lines.push(i18n.config.textProjectOverview);
      for (const item of configData.overview) {
        lines.push(`  ${item.label}: ${item.value}`);
      }
      lines.push("");
      lines.push(`${i18n.config.textEquipment} (${configData.equipment.length} ${i18n.config.items})`);
      for (const item_0 of configData.equipment) {
        lines.push(`  ${item_0.name} — ${item_0.meta} ×${item_0.quantity}`);
      }
      lines.push("");
      if (configData.notes.length > 0) {
        lines.push(i18n.config.textNotes);
        for (const note of configData.notes) {
          lines.push(`  [${note.tag}] ${note.content}`);
        }
      }
      navigator.clipboard.writeText(lines.join("\n")).then(() => {
        setCopied(true);
        toast.success(i18n.config.copiedToClipboard);
        setTimeout(() => setCopied(false), 2000);
      });
    };
    $[29] = configData;
    $[30] = i18n.config.copiedToClipboard;
    $[31] = i18n.config.items;
    $[32] = i18n.config.textConfiguration;
    $[33] = i18n.config.textEquipment;
    $[34] = i18n.config.textNotes;
    $[35] = i18n.config.textProjectOverview;
    $[36] = i18n.config.textStatus;
    $[37] = t7;
  } else {
    t7 = $[37];
  }
  const handleCopy = t7;
  let t8;
  if ($[38] !== isOpen || $[39] !== onClose) {
    t8 = isOpen && <div className="fixed inset-0 z-40 bg-black/20 md:hidden" onClick={onClose} />;
    $[38] = isOpen;
    $[39] = onClose;
    $[40] = t8;
  } else {
    t8 = $[40];
  }
  const t9 = isOpen ? "w-[50%] min-w-105 max-lg:w-full max-lg:min-w-0" : "w-0 min-w-0";
  let t10;
  if ($[41] !== t9) {
    t10 = cn("border-border bg-background shrink-0 overflow-hidden border-l transition-all duration-400 ease-in-out", "flex flex-col", "max-lg:fixed max-lg:inset-y-0 max-lg:right-0 max-lg:z-50", t9);
    $[41] = t9;
    $[42] = t10;
  } else {
    t10 = $[42];
  }
  const t11 = isOpen ? "opacity-100 delay-150" : "opacity-0";
  let t12;
  if ($[43] !== t11) {
    t12 = cn("flex h-full w-[50vw] min-w-105 flex-col transition-opacity duration-200 max-lg:w-full max-lg:min-w-0", t11);
    $[43] = t11;
    $[44] = t12;
  } else {
    t12 = $[44];
  }
  let t13;
  if ($[45] !== i18n.config.title) {
    t13 = <span className="text-foreground text-sm font-medium">{i18n.config.title}</span>;
    $[45] = i18n.config.title;
    $[46] = t13;
  } else {
    t13 = $[46];
  }
  let t14;
  if ($[47] !== configData) {
    t14 = configData?.status && <span className="border-border bg-muted text-muted-foreground rounded-full border px-2 py-0.5 text-[11px]">{configData.status}</span>;
    $[47] = configData;
    $[48] = t14;
  } else {
    t14 = $[48];
  }
  let t15;
  if ($[49] !== t13 || $[50] !== t14) {
    t15 = <div className="flex items-center gap-2.5">{t13}{t14}</div>;
    $[49] = t13;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  let t16;
  if ($[52] !== configMutation) {
    t16 = () => configMutation.mutate();
    $[52] = configMutation;
    $[53] = t16;
  } else {
    t16 = $[53];
  }
  const t17 = configMutation.isPending || chatIsPending;
  let t18;
  if ($[54] !== configMutation.isPending) {
    t18 = configMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />;
    $[54] = configMutation.isPending;
    $[55] = t18;
  } else {
    t18 = $[55];
  }
  let t19;
  if ($[56] !== i18n.config.refresh || $[57] !== t16 || $[58] !== t17 || $[59] !== t18) {
    t19 = <button onClick={t16} disabled={t17} className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-7.5 w-7.5 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50" title={i18n.config.refresh}>{t18}</button>;
    $[56] = i18n.config.refresh;
    $[57] = t16;
    $[58] = t17;
    $[59] = t18;
    $[60] = t19;
  } else {
    t19 = $[60];
  }
  let t20;
  if ($[61] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = <X className="h-4 w-4" />;
    $[61] = t20;
  } else {
    t20 = $[61];
  }
  let t21;
  if ($[62] !== onClose) {
    t21 = <button onClick={onClose} className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-7.5 w-7.5 items-center justify-center rounded-full transition-colors">{t20}</button>;
    $[62] = onClose;
    $[63] = t21;
  } else {
    t21 = $[63];
  }
  let t22;
  if ($[64] !== t19 || $[65] !== t21) {
    t22 = <div className="flex items-center gap-1">{t19}{t21}</div>;
    $[64] = t19;
    $[65] = t21;
    $[66] = t22;
  } else {
    t22 = $[66];
  }
  let t23;
  if ($[67] !== t15 || $[68] !== t22) {
    t23 = <div className="border-border flex shrink-0 items-center justify-between border-b px-5 py-3">{t15}{t22}</div>;
    $[67] = t15;
    $[68] = t22;
    $[69] = t23;
  } else {
    t23 = $[69];
  }
  let t24;
  if ($[70] !== chatIsPending || $[71] !== configData || $[72] !== configMutation || $[73] !== i18n.config.equipment || $[74] !== i18n.config.generateConfiguration || $[75] !== i18n.config.items || $[76] !== i18n.config.notes || $[77] !== i18n.config.projectOverview || $[78] !== i18n.config.refreshDiagram || $[79] !== i18n.config.renderingDiagram || $[80] !== i18n.config.signalFlow || $[81] !== i18n.config.signalFlowDiagramAlt || $[82] !== refreshSignalFlow || $[83] !== signalFlowError || $[84] !== signalFlowLoading || $[85] !== signalFlowPng) {
    t24 = <div className="slim flex-1 overflow-y-auto p-5">{configData ? <div className="flex flex-col gap-7">{configData.overview.length > 0 && <ConfigSection title={i18n.config.projectOverview}><div className="grid grid-cols-2 max-sm:grid-cols-1">{configData.overview.map(_temp)}</div></ConfigSection>}{configData.equipment.length > 0 && <ConfigSection title={i18n.config.equipment} count={`${configData.equipment.length} ${i18n.config.items}`}><div className="flex flex-col">{configData.equipment.map((item_2, i) => <div key={`${item_2.name}-${i}`} className={cn("flex items-center gap-2.5 py-2.5", i < configData.equipment.length - 1 && "border-border/60 border-b")}><div className={cn("h-1.5 w-1.5 shrink-0 rounded-full", CATEGORY_DOT[item_2.category])} /><div className="min-w-0 flex-1"><div className="text-foreground truncate text-[13px] font-medium">{item_2.name}</div><div className="text-muted-foreground text-[11px]">{item_2.meta}</div></div><span className="text-muted-foreground shrink-0 font-mono text-[11px]">×{item_2.quantity}</span></div>)}</div></ConfigSection>}{configData.signalFlow.length > 0 && <ConfigSection title={i18n.config.signalFlow} action={<button type="button" onClick={refreshSignalFlow} disabled={signalFlowLoading} className="text-muted-foreground hover:bg-muted hover:text-foreground flex h-7 w-7 items-center justify-center rounded-full transition-colors disabled:cursor-not-allowed disabled:opacity-50" title={i18n.config.refreshDiagram}><RefreshCw className={cn("h-3.5 w-3.5", signalFlowLoading && "animate-spin")} /></button>}><div className="flex flex-wrap items-center gap-0">{signalFlowPng ? <div className="border-border bg-card min-h-70 w-full max-w-none overflow-auto rounded-lg border px-4 py-3 shadow-sm"><img src={signalFlowPng} alt={i18n.config.signalFlowDiagramAlt} className="block h-auto w-full min-w-0 shrink-0 object-contain" /></div> : signalFlowError ? <div className="border-border bg-card text-muted-foreground flex min-h-70 w-full flex-col items-center justify-center gap-3 rounded-lg border text-sm"><p>{signalFlowError}</p><button type="button" onClick={refreshSignalFlow} className="bg-card border-border text-foreground hover:bg-accent inline-flex items-center gap-2 rounded-lg border px-3 py-1.5 text-xs"><RefreshCw className="h-3.5 w-3.5" />{i18n.config.refreshDiagram}</button></div> : <div className="border-border bg-card text-muted-foreground flex min-h-70 w-full items-center justify-center rounded-lg border text-sm">{i18n.config.renderingDiagram}</div>}</div></ConfigSection>}{configData.notes.length > 0 && <ConfigSection title={i18n.config.notes}><div className="flex flex-col">{configData.notes.map((note_0, i_0) => <p key={`${note_0.tag}-${i_0}`} className={cn("text-muted-foreground py-3 text-[13px] leading-relaxed", i_0 < configData.notes.length - 1 && "border-border/60 border-b")}><span className="bg-muted text-muted-foreground mr-1 inline-block rounded px-1.5 py-px font-mono text-[11px] font-medium uppercase">{note_0.tag}</span>{note_0.content}</p>)}</div></ConfigSection>}</div> : <div className="flex flex-1 items-center justify-center py-20"><button onClick={() => configMutation.mutate()} disabled={configMutation.isPending || chatIsPending} className="bg-card border-border text-foreground hover:bg-accent flex items-center gap-2 rounded-lg border px-4 py-2 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50">{configMutation.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}{i18n.config.generateConfiguration}</button></div>}</div>;
    $[70] = chatIsPending;
    $[71] = configData;
    $[72] = configMutation;
    $[73] = i18n.config.equipment;
    $[74] = i18n.config.generateConfiguration;
    $[75] = i18n.config.items;
    $[76] = i18n.config.notes;
    $[77] = i18n.config.projectOverview;
    $[78] = i18n.config.refreshDiagram;
    $[79] = i18n.config.renderingDiagram;
    $[80] = i18n.config.signalFlow;
    $[81] = i18n.config.signalFlowDiagramAlt;
    $[82] = refreshSignalFlow;
    $[83] = signalFlowError;
    $[84] = signalFlowLoading;
    $[85] = signalFlowPng;
    $[86] = t24;
  } else {
    t24 = $[86];
  }
  const t25 = configData?.updatedAt || i18n.config.updatedJustNow;
  let t26;
  if ($[87] !== t25) {
    t26 = <span className="text-muted-foreground text-[11px]">{t25}</span>;
    $[87] = t25;
    $[88] = t26;
  } else {
    t26 = $[88];
  }
  let t27;
  if ($[89] !== copied) {
    t27 = copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />;
    $[89] = copied;
    $[90] = t27;
  } else {
    t27 = $[90];
  }
  const t28 = copied ? i18n.config.copied : i18n.config.copy;
  let t29;
  if ($[91] !== handleCopy || $[92] !== i18n.config.copyConfiguration || $[93] !== t27 || $[94] !== t28) {
    t29 = <button onClick={handleCopy} title={i18n.config.copyConfiguration} className="border-border bg-background text-foreground hover:bg-muted flex items-center gap-1.5 rounded-lg border px-3.5 py-1.75 text-[13px] transition-colors">{t27}{t28}</button>;
    $[91] = handleCopy;
    $[92] = i18n.config.copyConfiguration;
    $[93] = t27;
    $[94] = t28;
    $[95] = t29;
  } else {
    t29 = $[95];
  }
  const t30 = signalFlowPng || null;
  let t31;
  if ($[96] !== configData || $[97] !== t30) {
    t31 = <ConfigPanelPdfExport data={configData} signalFlowImageUrl={t30} />;
    $[96] = configData;
    $[97] = t30;
    $[98] = t31;
  } else {
    t31 = $[98];
  }
  let t32;
  if ($[99] !== t29 || $[100] !== t31) {
    t32 = <div className="flex gap-1.5">{t29}{t31}</div>;
    $[99] = t29;
    $[100] = t31;
    $[101] = t32;
  } else {
    t32 = $[101];
  }
  let t33;
  if ($[102] !== t26 || $[103] !== t32) {
    t33 = <div className="border-border flex shrink-0 items-center justify-between border-t px-5 py-3">{t26}{t32}</div>;
    $[102] = t26;
    $[103] = t32;
    $[104] = t33;
  } else {
    t33 = $[104];
  }
  let t34;
  if ($[105] !== t12 || $[106] !== t23 || $[107] !== t24 || $[108] !== t33) {
    t34 = <div className={t12}>{t23}{t24}{t33}</div>;
    $[105] = t12;
    $[106] = t23;
    $[107] = t24;
    $[108] = t33;
    $[109] = t34;
  } else {
    t34 = $[109];
  }
  let t35;
  if ($[110] !== t10 || $[111] !== t34) {
    t35 = <aside className={t10}>{t34}</aside>;
    $[110] = t10;
    $[111] = t34;
    $[112] = t35;
  } else {
    t35 = $[112];
  }
  let t36;
  if ($[113] !== t35 || $[114] !== t8) {
    t36 = <>{t8}{t35}</>;
    $[113] = t35;
    $[114] = t8;
    $[115] = t36;
  } else {
    t36 = $[115];
  }
  return t36;
}

// ─── Sub-components ─────────────────────────────────────
function _temp(item_1) {
  return <div key={item_1.label} className="border-border/60 flex flex-col gap-px border-b py-2"><span className="text-muted-foreground text-[11px] font-medium tracking-wide uppercase">{item_1.label}</span><span className={cn("text-foreground text-[13px] font-medium", item_1.mono && "font-mono")}>{item_1.value}</span></div>;
}
function ConfigSection(t0) {
  const $ = _c(13);
  const {
    title,
    count,
    action,
    children
  } = t0;
  let t1;
  if ($[0] !== title) {
    t1 = <h3 className="text-foreground text-[13px] font-medium">{title}</h3>;
    $[0] = title;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== count) {
    t2 = count && <span className="text-muted-foreground text-[13px]">{count}</span>;
    $[2] = count;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  let t3;
  if ($[4] !== action || $[5] !== t2) {
    t3 = <div className="flex items-center gap-2">{action}{t2}</div>;
    $[4] = action;
    $[5] = t2;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  let t4;
  if ($[7] !== t1 || $[8] !== t3) {
    t4 = <div className="border-border/60 mb-3 flex items-baseline justify-between border-b pb-1.5">{t1}{t3}</div>;
    $[7] = t1;
    $[8] = t3;
    $[9] = t4;
  } else {
    t4 = $[9];
  }
  let t5;
  if ($[10] !== children || $[11] !== t4) {
    t5 = <section>{t4}{children}</section>;
    $[10] = children;
    $[11] = t4;
    $[12] = t5;
  } else {
    t5 = $[12];
  }
  return t5;
}